export type Platform = "mac" | "other";

interface NavigatorWithUAData {
  platform?: string;
  userAgentData?: { platform?: string };
}

/**
 * Detects whether the current platform uses ⌘ as its primary modifier.
 * Safe to call during render and in non-browser environments (falls back to `other`).
 */
export function detectPlatform(nav?: NavigatorWithUAData): Platform {
  const n =
    nav ?? (typeof navigator === "undefined" ? undefined : (navigator as NavigatorWithUAData));
  if (!n) return "other";
  const platform = n.userAgentData?.platform ?? n.platform ?? "";
  return /mac|iphone|ipad|ipod/i.test(platform) ? "mac" : "other";
}

const MAC_LABELS: Record<string, string> = {
  mod: "⌘",
  cmd: "⌘",
  command: "⌘",
  meta: "⌘",
  ctrl: "⌃",
  control: "⌃",
  alt: "⌥",
  option: "⌥",
  shift: "⇧",
  enter: "↩",
  return: "↩",
  backspace: "⌫",
  delete: "⌦",
  del: "⌦",
  escape: "⎋",
  esc: "⎋",
  tab: "⇥",
  space: "Space",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
  home: "↖",
  end: "↘",
  pageup: "⇞",
  pagedown: "⇟",
};

const OTHER_LABELS: Record<string, string> = {
  mod: "Ctrl",
  cmd: "Win",
  command: "Win",
  meta: "Win",
  ctrl: "Ctrl",
  control: "Ctrl",
  alt: "Alt",
  option: "Alt",
  shift: "Shift",
  enter: "Enter",
  return: "Enter",
  backspace: "Backspace",
  delete: "Del",
  del: "Del",
  escape: "Esc",
  esc: "Esc",
  tab: "Tab",
  space: "Space",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
  home: "Home",
  end: "End",
  pageup: "PgUp",
  pagedown: "PgDn",
};

/**
 * Splits a combo like `"mod+shift+k"` into display labels for the platform:
 * `["⌘", "⇧", "K"]` on Mac, `["Ctrl", "Shift", "K"]` elsewhere.
 */
export function shortcutKeys(combo: string, platform: Platform = detectPlatform()): string[] {
  const labels = platform === "mac" ? MAC_LABELS : OTHER_LABELS;
  return combo
    .split("+")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => {
      const key = part.toLowerCase();
      const label = labels[key];
      if (label !== undefined) return label;
      return part.length === 1 ? part.toUpperCase() : part;
    });
}

/**
 * Formats a combo for display: `"mod+k"` becomes `"⌘K"` on Mac and `"Ctrl+K"` elsewhere.
 */
export function formatShortcut(combo: string, platform: Platform = detectPlatform()): string {
  if (/\s/.test(combo.trim()))
    return combo
      .trim()
      .split(/\s+/)
      .map((chord) => formatShortcut(chord, platform))
      .join(" → ");
  const keys = shortcutKeys(combo, platform);
  return platform === "mac" ? keys.join("") : keys.join("+");
}
